// The call window: one named browser window per rep that shows the CRM and the coach side by side while a call is on
// (Allen, October 30-31, 2026). It opens from the Dial or Answer click itself, because Chrome blocks window.open
// outside a user gesture, and a second call reuses the same window without reloading it. Pure helpers with injected
// opener, storage, screen and channel, so every rule here runs under the node test runner.
import type { WindowOpener } from '../phone/contextCard.ts';

/** Not 'sidetone-pipeline' (the CRM page pop) and not 'sidetone-session-<uuid>' (calling-session controllers). */
export const CALL_WINDOW_NAME = 'sidetone-call-window';
export const CALL_WINDOW_CHANNEL = 'sidetone-call-window-v1';
/** Web Lock the newest call window takes with steal, so only one window polls the coach (GET /coach is 30 a minute). */
export const CALL_WINDOW_LOCK = 'sidetone-call-window-v1';
export const CALL_WINDOW_BOUNDS_KEY = 'sidetone.callWindow.bounds';
export const CALL_WINDOW_PATH = '/call-window';
export const CALL_WINDOW_DEFAULT = Object.freeze({ width: 1080, height: 720 });
/** A dial that has not produced a call ID within this long is no longer bound to the window. */
export const CALL_WINDOW_LAUNCH_TTL_MS = 60_000;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NONCE = /^[a-z0-9]{16}$/;

export const isCallId = (value: unknown): value is string => typeof value === 'string' && UUID.test(value);
export const isLaunchNonce = (value: unknown): value is string => typeof value === 'string' && NONCE.test(value);

export type CallWindowTarget = { kind: 'call'; callId: string } | { kind: 'pending'; launch: string };

/** /call-window/<call id> once the call has an ID; /call-window/pending?launch=<nonce> from the Dial click. */
export function callWindowPath(target: { callId: string } | { launch: string }): string {
  return 'callId' in target
    ? `${CALL_WINDOW_PATH}/${encodeURIComponent(target.callId)}`
    : `${CALL_WINDOW_PATH}/pending?launch=${encodeURIComponent(target.launch)}`;
}

/** The call window's own address, or null for every other page (including /call, the call-link page). */
export function parseCallWindowLocation(pathname: string, search: string): CallWindowTarget | null {
  if (typeof pathname !== 'string' || !pathname.startsWith(`${CALL_WINDOW_PATH}/`)) return null;
  const rest = pathname.slice(CALL_WINDOW_PATH.length + 1);
  if (!rest || rest.includes('/')) return null;
  let segment: string;
  try { segment = decodeURIComponent(rest); } catch { return null; }
  if (segment === 'pending') {
    let launch: string | null = null;
    try { launch = new URLSearchParams(search ?? '').get('launch'); } catch { return null; }
    return isLaunchNonce(launch) ? { kind: 'pending', launch } : null;
  }
  return isCallId(segment) ? { kind: 'call', callId: segment.toLowerCase() } : null;
}

export function isCallWindowDocument(pathname: unknown): boolean {
  return typeof pathname === 'string' && (pathname === CALL_WINDOW_PATH || pathname.startsWith(`${CALL_WINDOW_PATH}/`));
}

/** 16 lowercase letters and digits, from crypto when the browser has it. */
export function newLaunchNonce(random: () => number = Math.random): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = new Uint8Array(16);
  const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
  if (random === Math.random && cryptoObj?.getRandomValues) cryptoObj.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(random() * 256);
  return [...bytes].map(b => alphabet[b % alphabet.length]).join('');
}

// ---- Bounds ----

export interface WindowBounds { left: number; top: number; width: number; height: number }
export interface ScreenRect { availLeft: number; availTop: number; availWidth: number; availHeight: number }
export interface OpenerRect { screenX: number; screenY: number; outerWidth: number; outerHeight: number }

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(min, max));
/** At least this much of a saved window stays on a screen, so a window left on an unplugged monitor comes back. */
export const BOUNDS_VISIBLE_PX = 120;

/** The shortest first window: the call window's --cw-min-h, the height every slot fits in (CallWindowView.tsx). */
export const CALL_WINDOW_MIN_HEIGHT = 560;
/** The first window, with nothing saved: beside the opener's left edge so the dock's call controls stay visible, and
 * never shorter than CALL_WINDOW_MIN_HEIGHT (clampBounds still keeps it on a shorter screen). */
export function firstBounds(opener: OpenerRect, screen: ScreenRect): WindowBounds {
  const width = Math.round(clamp(opener.outerWidth - 452, 720, 1100));
  const height = Math.round(clamp(opener.outerHeight - 140, CALL_WINDOW_MIN_HEIGHT, 760));
  return clampBounds({ left: Math.round(opener.screenX + 16), top: Math.round(opener.screenY + 96), width, height }, screen);
}

/** Keeps a saved or computed window on the screen: never larger than the screen, never more than all but 120 px off it. */
export function clampBounds(bounds: WindowBounds, screen: ScreenRect): WindowBounds {
  const width = Math.round(clamp(bounds.width, 480, screen.availWidth));
  const height = Math.round(clamp(bounds.height, 360, screen.availHeight));
  const minLeft = screen.availLeft - width + BOUNDS_VISIBLE_PX;
  const maxLeft = screen.availLeft + screen.availWidth - BOUNDS_VISIBLE_PX;
  const minTop = screen.availTop;
  const maxTop = screen.availTop + screen.availHeight - BOUNDS_VISIBLE_PX;
  return { left: Math.round(clamp(bounds.left, minLeft, maxLeft)), top: Math.round(clamp(bounds.top, minTop, maxTop)), width, height };
}

type BoundsStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function readCallWindowBounds(storage: BoundsStorage | null): WindowBounds | null {
  try {
    const raw = JSON.parse(storage?.getItem(CALL_WINDOW_BOUNDS_KEY) ?? 'null') as Partial<WindowBounds> | null;
    if (!raw || typeof raw !== 'object') return null;
    const { left, top, width, height } = raw;
    if (![left, top, width, height].every(n => typeof n === 'number' && Number.isFinite(n))) return null;
    if ((width as number) < 100 || (height as number) < 100) return null;
    return { left: Math.round(left as number), top: Math.round(top as number), width: Math.round(width as number), height: Math.round(height as number) };
  } catch { return null; }
}

export function writeCallWindowBounds(storage: BoundsStorage | null, bounds: WindowBounds): void {
  try { storage?.setItem(CALL_WINDOW_BOUNDS_KEY, JSON.stringify(bounds)); } catch { /* Storage blocked: the next window opens at the default place. */ }
}

/** The feature string. Never "noopener": with it window.open returns null even when the window opened, so a blocked
 * pop-up could not be told apart from an open one. The browser ignores it for a window that already exists. */
export function callWindowFeatures(bounds: WindowBounds | null): string {
  const b = bounds ?? { ...CALL_WINDOW_DEFAULT, left: Number.NaN, top: Number.NaN };
  const parts = ['popup=yes', `width=${Math.round(b.width)}`, `height=${Math.round(b.height)}`];
  if (Number.isFinite(b.left) && Number.isFinite(b.top)) parts.push(`left=${Math.round(b.left)}`, `top=${Math.round(b.top)}`);
  return parts.join(',');
}

/** Saved bounds clamped to this screen, or the first-window place beside the opener. */
export function boundsForOpen(saved: WindowBounds | null, opener: OpenerRect | null, screen: ScreenRect | null): WindowBounds | null {
  if (!screen) return saved;
  if (saved) return clampBounds(saved, screen);
  return opener ? firstBounds(opener, screen) : null;
}

// ---- Opening ----

export type OpenCallWindowResult = 'opened' | 'reused' | 'blocked' | 'unsupported';

export interface OpenCallWindowOptions {
  open: WindowOpener | null;
  features: string;
  /** Absolute origin the path is joined to (location.origin). */
  origin: string;
  supported: boolean;
  /** The window this tab opened before, if it still has one. */
  existing?: Window | null;
}

const isClosed = (win: Window | null | undefined): boolean => {
  if (!win) return true;
  try { return win.closed === true; } catch { return true; }
};

/**
 * Opens or reuses the call window. Runs synchronously inside the click or key press.
 *
 * The window is first looked up with an empty URL (`window.open('', name)`), which never navigates an existing named
 * window: a call window that is already showing is reused in place and told what to show over the channel, so it
 * never reloads. Only a fresh about:blank window is pointed at `path`.
 */
export function openCallWindow(path: string, options: OpenCallWindowOptions): { result: OpenCallWindowResult; win: Window | null } {
  if (!options.supported) return { result: 'unsupported', win: null };
  let win: Window | null = isClosed(options.existing) ? null : options.existing ?? null;
  if (!win) {
    if (!options.open) return { result: 'blocked', win: null };
    try { win = options.open('', CALL_WINDOW_NAME, options.features); } catch { win = null; }
  }
  if (!win) return { result: 'blocked', win: null };
  let reused = false;
  try { reused = isCallWindowDocument(win.location.pathname); } catch { reused = false; }
  if (!reused) {
    try { win.location.replace(options.origin.replace(/\/+$/, '') + path); } catch { return { result: 'blocked', win: null }; }
  }
  try { win.focus(); } catch { /* the browser decides */ }
  return { result: reused ? 'reused' : 'opened', win };
}

// ---- Messages between the main window and the call window ----

export type CallWindowMessage =
  | { v: 1; account: string; type: 'hello'; launch: string }
  | { v: 1; account: string; type: 'bound'; launch: string; callId: string }
  | { v: 1; account: string; type: 'bind'; launch: string; callId: string }
  | { v: 1; account: string; type: 'failed'; launch: string; message: string }
  | { v: 1; account: string; type: 'show'; callId: string }
  | { v: 1; account: string; type: 'pending'; launch: string }
  | { v: 1; account: string; type: 'gone' };

/** A well-formed message, or null. Everything that crosses the channel is checked: it is another document's data. */
export function readCallWindowMessage(raw: unknown): CallWindowMessage | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  if (m.v !== 1 || typeof m.account !== 'string' || !m.account || m.account.length > 200) return null;
  const account = m.account;
  switch (m.type) {
    case 'hello': return isLaunchNonce(m.launch) ? { v: 1, account, type: 'hello', launch: m.launch } : null;
    case 'pending': return isLaunchNonce(m.launch) ? { v: 1, account, type: 'pending', launch: m.launch } : null;
    case 'bound': return isLaunchNonce(m.launch) && isCallId(m.callId) ? { v: 1, account, type: 'bound', launch: m.launch, callId: m.callId } : null;
    case 'bind': return isLaunchNonce(m.launch) && isCallId(m.callId) ? { v: 1, account, type: 'bind', launch: m.launch, callId: m.callId } : null;
    case 'failed': return isLaunchNonce(m.launch) && typeof m.message === 'string' ? { v: 1, account, type: 'failed', launch: m.launch, message: m.message.slice(0, 300) } : null;
    case 'show': return isCallId(m.callId) ? { v: 1, account, type: 'show', callId: m.callId } : null;
    case 'gone': return { v: 1, account, type: 'gone' };
    default: return null;
  }
}

export interface ChannelLike {
  postMessage(message: unknown): void;
  close(): void;
  onmessage: ((event: { data: unknown }) => void) | null;
}

export function browserChannel(): ChannelLike | null {
  try { return typeof BroadcastChannel === 'undefined' ? null : (new BroadcastChannel(CALL_WINDOW_CHANNEL) as unknown as ChannelLike); } catch { return null; }
}

// ---- The main window's link to its call window ----

export type CallWindowStatus = 'idle' | 'opened' | 'blocked' | 'off' | 'closed' | 'unsupported';

export interface CallWindowLinkSnapshot {
  status: CallWindowStatus;
  /** The call the window shows or will show. */
  callId: string | null;
  /** The Dial launch still waiting for its call ID. */
  launch: string | null;
}

export interface EngineCallLike { key: string; callId: string | null; origin: 'outbound' | 'inbound' }

export interface CallWindowLinkDeps {
  opener: () => WindowOpener | null;
  channel: () => ChannelLike | null;
  supported: () => boolean;
  origin: () => string;
  features: () => string;
  now?: () => number;
  nonce?: () => string;
}

const IDLE: CallWindowLinkSnapshot = Object.freeze({ status: 'idle', callId: null, launch: null });

/**
 * The main window's side of the call window, held in the module so it outlives the Dialer, which unmounts as soon as
 * the engine has the call. Records what the window should show, opens it from a gesture, and binds an outbound call's
 * ID once the server confirms it (over the channel, so the window never reloads).
 */
export class CallWindowLink {
  private state: CallWindowLinkSnapshot = IDLE;
  private readonly listeners = new Set<() => void>();
  private win: Window | null = null;
  private account: string | null = null;
  private knownKeys = new Set<string>();
  private launchedAt = 0;
  private bindAcked = false;
  /** Why the current launch failed, kept so a window that loads after the refusal still hears it on its hello. */
  private failure: string | null = null;
  private chan: ChannelLike | null = null;
  private readonly now: () => number;
  private readonly nonce: () => string;

  constructor(private readonly deps: CallWindowLinkDeps) {
    this.now = deps.now ?? Date.now;
    this.nonce = deps.nonce ?? (() => newLaunchNonce());
  }

  getSnapshot = (): CallWindowLinkSnapshot => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  private set(patch: Partial<CallWindowLinkSnapshot>): void {
    const next = { ...this.state, ...patch };
    if (next.status === this.state.status && next.callId === this.state.callId && next.launch === this.state.launch) return;
    this.state = next;
    for (const l of [...this.listeners]) l();
  }

  private channel(): ChannelLike | null {
    if (this.chan) return this.chan;
    const chan = this.deps.channel();
    if (!chan) return null;
    chan.onmessage = event => this.receive(event.data);
    this.chan = chan;
    return chan;
  }

  private post(message: CallWindowMessage): void {
    try { this.channel()?.postMessage(message); } catch { /* the window catches up from its own stream */ }
  }

  private receive(raw: unknown): void {
    const message = readCallWindowMessage(raw);
    if (!message || message.account !== this.account) return;
    if (message.type === 'hello' && message.launch === this.state.launch) {
      // A window this tab has no handle for (opened before a reload, or followed a dial made without a click) is alive.
      if (this.state.status !== 'opened' && this.state.status !== 'unsupported') this.set({ status: 'opened' });
      // BroadcastChannel keeps nothing for a document that was not listening yet: answer every hello with what it missed.
      if (this.failure !== null && !this.state.callId) this.post({ v: 1, account: message.account, type: 'failed', launch: message.launch, message: this.failure });
      else if (this.state.callId && !this.bindAcked) this.post({ v: 1, account: message.account, type: 'bind', launch: message.launch, callId: this.state.callId });
    } else if (message.type === 'bound' && message.launch === this.state.launch && message.callId === this.state.callId) {
      this.bindAcked = true;
    } else if (message.type === 'gone') {
      if (this.state.status === 'opened') this.set({ status: 'closed' });
    }
  }

  /**
   * The Dialer's Call: a new launch. `open` (inside the gesture, preference on) may open the window; `follow` (the
   * preference on, no gesture needed) moves a call window that is already open to this call, because posting to it
   * needs no click: a calling session's auto-dials have no gesture, and without this every call after the first would
   * leave the window on the previous prospect. Defaults to `open`.
   */
  armDial({ account, knownKeys, open, follow = open }: { account: string; knownKeys: readonly string[]; open: boolean; follow?: boolean }): void {
    this.account = account;
    this.knownKeys = new Set(knownKeys);
    this.launchedAt = this.now();
    this.bindAcked = false;
    this.failure = null;
    const launch = this.nonce();
    this.set({ callId: null, launch, status: this.state.status === 'unsupported' ? 'unsupported' : 'off' });
    this.channel();
    const message: CallWindowMessage = { v: 1, account, type: 'pending', launch };
    if (open) this.show(callWindowPath({ launch }), message);
    else if (!this.deps.supported()) this.set({ status: 'unsupported' });
    else if (follow) this.follow(message);
  }

  /** Answer (button, call bar or Alt+Shift+A): the call ID is known, so the window opens straight at the call. */
  armAnswer({ account, callId, open, follow = open }: { account: string; callId: string; open: boolean; follow?: boolean }): void {
    this.account = account;
    this.knownKeys = new Set();
    this.launchedAt = 0;
    this.bindAcked = true;
    this.failure = null;
    this.set({ callId, launch: null, status: this.state.status === 'unsupported' ? 'unsupported' : 'off' });
    this.channel();
    const message: CallWindowMessage = { v: 1, account, type: 'show', callId };
    if (open) this.show(callWindowPath({ callId }), message);
    else if (!this.deps.supported()) this.set({ status: 'unsupported' });
    else if (follow) this.follow(message);
  }

  /**
   * No gesture, so no window.open: tell a call window that is already open what to show. With a live handle the dock row
   * says it is open; without one (this tab reloaded since it opened the window) the message still reaches the window over
   * the channel, and the window's hello for a pending launch marks it open. Never focuses: the rep stays where they are.
   */
  private follow(message: CallWindowMessage): void {
    if (this.win && isClosed(this.win)) this.win = null;
    this.post(message);
    if (this.win) this.set({ status: 'opened' });
  }

  /** The dock's "Open call window" button: always opens, whatever the preference. Focuses a window that is open. */
  openNow({ account, callId }: { account: string; callId: string | null }): void {
    this.account = account;
    const id = callId ?? this.state.callId;
    if (id && id !== this.state.callId) { this.knownKeys = new Set(); this.launch(null); this.set({ callId: id }); }
    this.channel();
    if (id) this.show(callWindowPath({ callId: id }), { v: 1, account, type: 'show', callId: id });
    else if (this.state.launch) this.show(callWindowPath({ launch: this.state.launch }), { v: 1, account, type: 'pending', launch: this.state.launch });
  }

  private launch(launch: string | null): void { this.set({ launch }); }

  private show(path: string, message: CallWindowMessage): void {
    const supported = this.deps.supported();
    const { result, win } = openCallWindow(path, {
      open: this.deps.opener(),
      features: this.deps.features(),
      origin: this.deps.origin(),
      supported,
      existing: this.win,
    });
    if (result === 'unsupported') { this.set({ status: 'unsupported' }); return; }
    if (result === 'blocked') { this.set({ status: 'blocked' }); return; }
    this.win = win;
    this.set({ status: 'opened' });
    if (result === 'reused') this.post(message);
  }

  /** Engine calls after a Dial: the first new outbound call with a server ID is the one the window shows. */
  observe(calls: readonly EngineCallLike[], failedStart: { key: string; message: string } | null): void {
    const { launch, callId } = this.state;
    if (!launch || !this.account) return;
    if (this.now() - this.launchedAt > CALL_WINDOW_LAUNCH_TTL_MS && !callId) { this.set({ launch: null }); return; }
    if (callId) return;
    const bound = calls.find(c => c.origin === 'outbound' && isCallId(c.callId) && !this.knownKeys.has(c.key));
    if (bound?.callId) {
      this.set({ callId: bound.callId });
      this.post({ v: 1, account: this.account, type: 'bind', launch, callId: bound.callId });
      return;
    }
    if (failedStart && !this.knownKeys.has(failedStart.key) && this.failure === null) {
      this.failure = failedStart.message;
      this.post({ v: 1, account: this.account, type: 'failed', launch, message: failedStart.message });
    }
  }

  /** The Dial was refused before the engine had a call (no failedStart follows): a pending window says so in place.
   * Kept for the launch, so a window still loading hears it on its first hello. */
  failLaunch(message: string): void {
    const { launch, callId } = this.state;
    if (!launch || callId || !this.account || this.failure !== null) return;
    this.failure = message;
    this.post({ v: 1, account: this.account, type: 'failed', launch, message });
  }

  /** The window was closed by the rep: the dock row says so and its button reopens it. */
  refreshClosed(): void {
    if (this.win && isClosed(this.win)) {
      this.win = null;
      if (this.state.status === 'opened') this.set({ status: 'closed' });
    }
  }

  /** Test and sign-out hook. */
  reset(): void {
    this.win = null;
    this.account = null;
    this.knownKeys = new Set();
    this.failure = null;
    this.state = IDLE;
    try { this.chan?.close(); } catch { /* already closed */ }
    this.chan = null;
    for (const l of [...this.listeners]) l();
  }
}

function browserScreen(): ScreenRect | null {
  if (typeof window === 'undefined' || !window.screen) return null;
  const s = window.screen as Screen & { availLeft?: number; availTop?: number };
  return { availLeft: s.availLeft ?? 0, availTop: s.availTop ?? 0, availWidth: s.availWidth, availHeight: s.availHeight };
}

function browserStorage(): BoundsStorage | null {
  try { return typeof window === 'undefined' ? null : window.localStorage; } catch { return null; }
}

/** Opening a window does nothing useful in the desktop app (its main process denies every window.open) or on iPhone
 * (switching away from the tab can background the call). */
export function callWindowSupported(): boolean {
  if (typeof window === 'undefined' || typeof window.open !== 'function') return false;
  try {
    if (window.location.protocol === 'app:') return false;
    if ((window as unknown as { sidetoneDesktop?: unknown }).sidetoneDesktop) return false;
    const ua = window.navigator?.userAgent ?? '';
    if (/iPhone|iPod/.test(ua)) return false;
  } catch { return false; }
  return true;
}

export function callWindowUnsupportedText(): string {
  if (typeof window !== 'undefined' && /iPhone|iPod/.test(window.navigator?.userAgent ?? '')) return 'Not available on iPhone';
  return 'Not available in the desktop app yet';
}

export const callWindowLink = new CallWindowLink({
  opener: () => (typeof window === 'undefined' || typeof window.open !== 'function' ? null : (url, target, features) => window.open(url, target, features)),
  channel: browserChannel,
  supported: callWindowSupported,
  origin: () => (typeof window === 'undefined' ? '' : window.location.origin),
  features: () => {
    if (typeof window === 'undefined') return callWindowFeatures(null);
    const opener: OpenerRect = { screenX: window.screenX, screenY: window.screenY, outerWidth: window.outerWidth, outerHeight: window.outerHeight };
    return callWindowFeatures(boundsForOpen(readCallWindowBounds(browserStorage()), opener, browserScreen()));
  },
});

/** `${tenant}:${user}`: the account a message belongs to (another account's window ignores it). */
export const callWindowAccount = (tenantId: string | null | undefined, userId: string | null | undefined): string => `${tenantId ?? ''}:${userId ?? ''}`;
